import {Component, ViewChild} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {RefundSearchResultPage, RefundSearchResultView} from 'web-angular-client';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {RefundSearchService} from '../../../sdk/component/search/refund-search.service';
import {SearchFilterBase} from '../../../sdk/component/search/search-filter-base';
import {SearchForm} from '../../../sdk/component/search/searchForm';
import {SearchService} from '../../../sdk/component/search/search.service';
import {TableSortChange} from '../../../sdk/component/table/sortable-table-base';
import {RefundRowActionsService} from '../../refund/refund-row-actions.service';

/**
 * Refund transactions list page. A host on the shared search infrastructure: the projected
 * {@code app-refund-search} filter drives the search and {@code app-refund-table} renders the current page.
 * Per-row actions (view account, cancel, copy refund link) live in the shared {@link RefundRowActionsService}; this
 * page adds an expandable detail row (refund link, expiry, failure reason, refund key, last updated).
 */
@Component({
    selector: 'app-refund-transactions',
    templateUrl: './refund-transactions.component.html',
    styleUrls: ['./refund-transactions.component.scss'],
    standalone: false
})
export class RefundTransactionsComponent extends SearchForm
{
    /** Refund ids whose detail row is currently expanded. */
    readonly expandedRefundIds = new Set<number>();

    /** The projected filter, used to re-run the search after a refund is cancelled. */
    @ViewChild('app') private filter?: SearchFilterBase;

    constructor(
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        refundSearchService: RefundSearchService,
        protected searchService: SearchService,
        private readonly refundRowActions: RefundRowActionsService)
    {
        super(authenticationService, refundSearchService, searchService, environmentService);
    }

    get contents(): RefundSearchResultView[]
    {
        const page = this.results as RefundSearchResultPage;
        return page?.content ?? [];
    }

    onSortChange(event: TableSortChange, searchComponent: SearchFilterBase): void
    {
        this.searchService.field = event.field;
        this.searchService.direction = event.order;
        searchComponent.refresh();
    }

    readonly buildMenuItemsForRefund = (row: RefundSearchResultView): MenuItem[] =>
        this.refundRowActions.buildMenuItems(row, () => this.filter?.refresh());

    /**
     * Copies the customer-facing refund link to the clipboard. Delegates to the shared row-actions service so the
     * inline "Copy" button in the expanded detail row keeps working (the context-menu action already routes through
     * the service).
     */
    copyRefundLink(row: RefundSearchResultView): void
    {
        this.refundRowActions.copyRefundLink(row);
    }

    readonly isRefundExpanded = (row: RefundSearchResultView): boolean =>
        row?.refund?.id != null && this.expandedRefundIds.has(row.refund.id);

    onRowClick(row: RefundSearchResultView): void
    {
        const id = row?.refund?.id;
        if (id == null)
        {
            return;
        }
        if (this.expandedRefundIds.has(id))
        {
            this.expandedRefundIds.delete(id);
        }
        else
        {
            this.expandedRefundIds.add(id);
        }
    }

    create(): void
    {
    }

    view(): void
    {
    }

    getDefaultSortField(): string
    {
        return 'dateTimeCreated';
    }
}
